import { contacts } from "@constants/contacts";
import { routes } from "@constants/routes";
import { CheckboxInput, handleApiRequest, Modal, useAuth, useMobile, UserTypeEnum } from "@cpj-ai/panel-npm-package";
import {
  UpdateProfileTermsStatusMutation,
  UpdateProfileTermsStatusMutationVariables,
  useUpdateProfileTermsStatusMutation,
} from "@graphql/generated";
import { Button, useDisclosure } from "@heroui/react";
import { useProfile } from "@hooks/useProfile";
import { parsePhoneNumber } from "libphonenumber-js";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const UserConsentModal = ({ onAccept }: { onAccept?: () => void }) => {
  const { isOpen, onClose, onOpen, onOpenChange } = useDisclosure();
  const { logout, isAuthenticated } = useAuth({ userType: UserTypeEnum.USER });
  const { profile } = useProfile();
  const [updateProfileTermsStatus, { loading: updateProfileTermsStatusLoading }] =
    useUpdateProfileTermsStatusMutation();
  const [accepted, setAccepted] = useState(false);
  const isMobile = useMobile();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || profile.acceptTermsAndConditions || !profile.isOnboardingCompleted) {
      onClose();
    } else {
      onOpen();
    }
  }, [isAuthenticated, onClose, onOpen, profile.acceptTermsAndConditions, profile.isOnboardingCompleted]);

  const onAcceptHandle = useCallback(async () => {
    await handleApiRequest<UpdateProfileTermsStatusMutation, UpdateProfileTermsStatusMutationVariables>({
      apiFunction: updateProfileTermsStatus,
      variables: {
        acceptTermsAndConditions: true,
      },
      componentName: "TermsAndConditionsModal",
      actionName: "acceptTerms",
      successMessage: "Terms and conditions accepted successfully",
      fallbackErrorMessage: "Failed to accept terms and conditions. Please try again.",
      onSuccess: () => {
        onClose();
        onAccept?.();
      },
    });
  }, [updateProfileTermsStatus, onClose, onAccept]);

  const handleDisagree = useCallback(() => {
    logout();
    onClose();
    navigate(routes.auth.main);
  }, [logout, onClose, navigate]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpenChange={onOpenChange}
      isDismissable={false}
      label="Terms of Use"
      size={isMobile ? "full" : "4xl"}
      bodyContent={
        <div className="space-y-6 p-4">
          <p className="font-medium text-sm">
            <strong>Last Updated:</strong> 2025-01-01
          </p>

          <div className="text-gray-700 leading-relaxed">
            <p className="text-sm md:text-base">
              Welcome to Canada Prime Jobs (“CPJ”), a platform designed to connect job seekers with prospective
              employers and provide a range of career development tools, including AI-driven interview practice,
              credential verification services, and post-hire training. By accessing or using the CPJ website, mobile
              application, or any related services (collectively, the “Services”), you agree to be bound by the
              following Terms of Use (“Terms”). If you do not agree with these Terms, do not use our Services.
            </p>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">1. Definitions:</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li className="text-sm md:text-base">
                <strong>“User,” “You,” or “Your”</strong> refers to any individual, candidate, or employer accessing or
                using CPJ’s Services.
              </li>
              <li className="text-sm md:text-base">
                <strong>“Candidate”</strong> refers to a job seeker or individual who registers or uses CPJ’s Services
                to find employment or enhance professional skills.
              </li>
              <li className="text-sm md:text-base">
                <strong>“Employer”</strong> refers to any person, company, or entity that posts job listings or
                otherwise uses CPJ’s Services to recruit and hire candidates.
              </li>
              <li className="text-sm md:text-base">
                <strong>“We,” “Us,” or “Our”</strong> refers to CPJ (Canada Prime Jobs).
              </li>
            </ul>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">2. Eligibility and Account Registration:</h2>

            <div className="space-y-2 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. Eligibility:</strong> You must be at least the age of majority in your province or territory
                of residence (or have the consent of a legal guardian) to use the Services. By registering, you
                represent and warrant that you meet all eligibility requirements.
              </p>

              <p className="text-sm md:text-base">
                <strong>2. Account Creation:</strong> To access certain Services, you may be required to create an
                account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  Provide accurate, current, and complete information during registration.
                </li>
                <li className="text-sm md:text-base">
                  Promptly update any information that becomes inaccurate or outdated.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. Login Credentials:</strong> You are responsible for maintaining the confidentiality of your
                account credentials (e.g., username, password). You agree to notify us immediately of any unauthorized
                access to or use of your account.
              </p>

              <p className="text-sm md:text-base">
                <strong>4. Identity Verification:</strong> By providing personal information such as your name, email,
                phone number, and any additional documentation, you authorize CPJ to use this information to verify your
                identity and facilitate the hiring process. We reserve the right to suspend or terminate accounts where
                identity information is incomplete or suspect.
              </p>
            </div>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">3. Description of Services:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. Profile Completion and Resume Creation:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  If you already have a resume, you may upload it to your CPJ account.
                </li>
                <li className="text-sm md:text-base">
                  If you do not have a resume, you can fill in the required sections (education, experience, skills,
                  etc.) and CPJ will generate a professional resume on your behalf.
                </li>
                <li className="text-sm md:text-base">
                  This information is used to match you with job opportunities aligned with your skills and background.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. AI Interview Tool:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  Our AI Interview tool provides personalized interview questions based on your desired job or industry.
                </li>
                <li className="text-sm md:text-base">
                  It analyzes your responses and offers feedback on communication skills, content clarity, and overall
                  performance.
                </li>
                <li className="text-sm md:text-base">
                  This tool is for training and practice purposes only; CPJ does not guarantee improved hiring outcomes
                  or employment offers.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. Post-Hiring Support:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ offers continuous training, skill development courses, and career guidance even after you secure
                  employment.
                </li>
                <li className="text-sm md:text-base">
                  Participation is voluntary; no aspect of the post-hire support creates an employment relationship
                  between you and CPJ.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>4. Credential Verification Services:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  You may upload educational or certification documents for verification.
                </li>
                <li className="text-sm md:text-base">
                  CPJ will take commercially reasonable steps to verify the authenticity of your documents within one
                  working week. If verification cannot be completed in that timeframe, your documents may be
                  automatically marked as verified based on your claim; however, CPJ reserves the right to reopen
                  verification at any time if new information suggests potential fraud.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>5. Training and Assessment Services:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ provides general and job-specific assessments to help you identify and improve your skill gaps.
                </li>
                <li className="text-sm md:text-base">
                  Participation is optional, but some Employers may require completion of assessments for certain
                  positions.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>6. Job Matching:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ may use AI-driven algorithms to match Candidates with suitable Employers or job postings.
                </li>
                <li className="text-sm md:text-base">
                  We make no guarantee of job placement, nor do we warrant the availability or suitability of any job
                  for any particular Candidate.
                </li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">4. User Responsibilities:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. Accuracy of Information:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  You are solely responsible for ensuring that all information, documents, and materials you provide
                  (including resumes, verification documents, and personal data) are truthful, accurate, and complete.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Use of Services:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  You agree to use the Services only for lawful purposes, in compliance with these Terms and any
                  applicable local, provincial, or federal laws in Canada or your jurisdiction.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. No Interference:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  You agree not to disrupt, modify, or interfere with the functionality or security of the Services.
                </li>
                <li className="text-sm md:text-base">
                  Automated scraping, data mining, or any unauthorized extraction of content is strictly prohibited.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>4. Authenticity of Credentials:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  You affirm that any educational or certification documents uploaded are genuine and unaltered, issued
                  by accredited institutions.
                </li>
                <li className="text-sm md:text-base">
                  Providing fraudulent or tampered documents is strictly prohibited and may result in account suspension
                  or ban, and possible legal action.
                </li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">
              5. Uploading Educational and Certification Documents:
            </h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                By uploading any educational or certification documents, you expressly agree to the following:
              </p>

              <p className="text-sm md:text-base">
                <strong>1. Authenticity:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  All uploaded documents are genuine, unaltered, and validly issued.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Compliance with Canadian Regulations:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  You understand that use of fraudulent documents can violate Canadian laws and may lead to serious
                  legal consequences, including rejection of your application.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. Site Policies:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  You agree to abide by all site rules and guidelines, which require maintaining the authenticity and
                  accuracy of your documents.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>4. Verification Process and Timelines:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  You acknowledge CPJ’s right to verify these documents within one (1) working week.
                </li>
                <li className="text-sm md:text-base">
                  If CPJ cannot complete verification within that time, your documents may be tentatively approved based
                  on your claims, but remain subject to further verification at CPJ’s discretion.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>5. Consequences of Violations:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  Any attempt to upload deceptive or unauthorized documents may lead to immediate suspension or
                  permanent ban of your account.
                </li>
                <li className="text-sm md:text-base">
                  If warranted, CPJ may report such incidents to relevant authorities for further investigation.
                </li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">6. Privacy and Data Security:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. Collection of Personal Data:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ collects personal data such as names, emails, phone numbers, resumes, and uploaded credentials to
                  facilitate recruitment and provide better matching services.
                </li>
                <li className="text-sm md:text-base">
                  By using our Services, you consent to the collection, use, and storage of your personal data in
                  accordance with our Privacy Policy.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Storage and Encryption:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  We take commercially reasonable measures to ensure the confidentiality and security of your personal
                  information, including encryption of sensitive data at rest and in transit.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. Disclosure to Employers:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  Your information will only be disclosed to potential employers with your explicit or implied
                  permission (e.g., when you apply for a job or make your profile visible).
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>4. Compliance with Laws:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ abides by applicable privacy legislation in Canada, including the Personal Information Protection
                  and Electronic Documents Act (PIPEDA) and, where relevant, British Columbia’s Personal Information
                  Protection Act (PIPA).
                </li>
                <li className="text-sm md:text-base">
                  We do not sell or rent your personal information to third parties for marketing purposes without your
                  consent.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>5. Data Retention:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  We retain personal data for as long as is necessary to fulfill the purposes for which it was collected
                  or as required by law.
                </li>
                <li className="text-sm md:text-base">
                  Upon account closure, some data may remain in backup or archival storage for legal or compliance
                  reasons.
                </li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">7. Use of AI Interview and Assessment Tools:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. No Guarantee of Success:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  The AI Interview tool and assessments are informational and advisory. CPJ does not guarantee that
                  using these tools will result in employment offers, improved interview performance, or any specific
                  outcome.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Accuracy and Limitations:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  While we strive to provide accurate feedback, algorithmic tools can have limitations. Users should
                  exercise judgment and not solely rely on automated advice.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. Intellectual Property Rights:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ retains all intellectual property rights in and to the AI tools and any related content. You may
                  not reverse engineer, reproduce, or distribute any part of these tools without our written permission.
                </li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">8. Employer-Related Terms:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. Job Postings and Recruitment Services:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  Employers may post job listings or use CPJ’s recruitment services subject to separate or additional
                  agreements (e.g., subscription plans, fees).
                </li>
                <li className="text-sm md:text-base">
                  Employers are responsible for complying with all relevant labor and employment laws, including
                  non-discrimination and fair hiring practices.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Candidate Information:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  Employers agree to use candidate information solely for legitimate recruiting and hiring purposes and
                  to maintain the confidentiality of candidate data.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. Compliance with Labor Laws:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ is not liable for any Employer’s non-compliance with applicable labor or employment laws.
                </li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">9. Disclaimers and Limitation of Liability:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. No Warranty of Job Placement:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ does not guarantee that using the Services will lead to employment, specific job offers, or any
                  particular outcome.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Accuracy of User-Provided Data:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  All resumes, profiles, and documents are provided by Users. CPJ makes reasonable efforts to verify
                  credentials when requested but cannot ensure complete accuracy of user-submitted data.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. “As Is” Basis:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  The Services are provided on an “as is” and “as available” basis. CPJ disclaims all warranties,
                  express or implied, including warranties of merchantability, fitness for a particular purpose, and
                  non-infringement.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>4. Limitation of Liability:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  To the fullest extent permitted by law, CPJ, its officers, directors, employees, associates, or agents
                  shall not be liable for any indirect, incidental, special, or consequential damages arising out of or
                  in connection with your use of the Services.
                </li>
                <li className="text-sm md:text-base">
                  In no event shall CPJ’s total liability to you exceed the amounts paid by you (if any) for access to
                  or use of the Services during the twelve (12) months preceding the event giving rise to liability.
                </li>
              </ul>
            </div>

            <h2 className="font-semibold text-base md:text-xl">10. Modification or Termination of Services:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. Changes to the Terms:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ reserves the right to update or modify these Terms at any time. We will provide notice of material
                  changes by posting the new Terms on the site and updating the “Last Updated” date.
                </li>
                <li className="text-sm md:text-base">
                  By continuing to use the Services after changes have been posted, you accept and agree to the revised
                  Terms.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Suspension or Termination of Accounts:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ may suspend or terminate your account if you violate these Terms, engage in fraudulent or illegal
                  conduct, or if we discontinue the Services for any reason.
                </li>
                <li className="text-sm md:text-base">
                  You may also terminate your account at any time by contacting us or using the account settings.
                </li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">11. Intellectual Property and Proprietary Rights:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. Ownership:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  All materials on the CPJ platform, including logos, text, graphics, software code, AI tools, and
                  design elements, are the exclusive property of CPJ or its licensors.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Limited License:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ grants you a personal, non-transferable, revocable license to access and use the Services for
                  lawful, personal, and non-commercial purposes.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. Prohibitions:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  You may not copy, distribute, modify, or create derivative works from any part of the CPJ Services
                  without explicit written permission from CPJ.
                </li>
              </ul>
            </div>

            <h2 className="font-semibold text-base md:text-xl">12. Governing Law and Dispute Resolution:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. Governing Law:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  These Terms shall be governed by and construed in accordance with the laws of the Province of British
                  Columbia and the federal laws of Canada.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Dispute Resolution:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  Any disputes arising from or relating to these Terms or the Services shall be resolved in the courts
                  of competent jurisdiction in British Columbia, unless otherwise agreed in a separate written
                  agreement.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. Waiver of Class Actions:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  To the extent permissible by law, you agree that any dispute or claim shall be brought on an
                  individual basis, not as a plaintiff or class member in any purported class or representative
                  proceeding.
                </li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div>
            <h2 className="font-semibold text-base md:text-xl">13. Miscellaneous:</h2>

            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                <strong>1. Severability:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  If any provision of these Terms is found to be invalid or unenforceable, that provision shall be
                  enforced to the maximum extent permissible, and the remaining provisions shall continue in full force
                  and effect.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>2. Entire Agreement:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  These Terms, together with our Privacy Policy and any additional agreements you enter into with CPJ,
                  constitute the entire agreement between you and CPJ concerning your use of the Services.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>3. No Waiver:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ’s failure to enforce any provision of these Terms shall not be considered a waiver of that
                  provision.
                </li>
              </ul>

              <p className="text-sm md:text-base">
                <strong>4. Force Majeure:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-sm md:text-base">
                  CPJ shall not be liable for any delay or failure to perform resulting from causes outside its
                  reasonable control, including but not limited to acts of God, war, terrorism, riots, embargoes,
                  government regulations, fire, floods, or strikes.
                </li>
              </ul>
            </div>
            <hr className="my-4" />
            <h2 className="font-semibold text-base md:text-xl">14. Contact Information:</h2>
            <div className="space-y-4 text-gray-700">
              <p className="text-sm md:text-base">
                If you have any questions, concerns, or comments about these Terms of Use or the CPJ Services, you may
                contact us at:
              </p>
              <ul className="space-y-2">
                <li className="text-sm md:text-base">
                  <span className="font-medium">Address:</span> {contacts.address}
                </li>
                <li className="text-sm md:text-base">
                  <span className="font-medium">Phone:</span>{" "}
                  <a href={`tel:${contacts.phone}`} className="text-blue-600 hover:text-blue-800">
                    {parsePhoneNumber(contacts.phone, "CA").formatInternational()}
                  </a>
                </li>
                <li className="text-sm md:text-base">
                  <span className="font-medium">Email:</span>{" "}
                  <a href={`mailto:${contacts.supportEmail}`} className="text-blue-600 hover:text-blue-800">
                    {contacts.supportEmail}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="space-y-4 text-gray-700">
            <p className="font-medium">
              By accessing or using CPJ’s Services, you acknowledge that you have read, understood, and agreed to be
              bound by these Terms of Use. If you do not agree, please discontinue use of the CPJ platform immediately.
            </p>
          </div>
        </div>
      }
      footerContent={
        <div className="w-full">
          <div>
            <CheckboxInput
              label="I consent to the collection, use, and disclosure of my personal information as described in this policy."
              onChange={(e) => setAccepted(e.target.checked)}
              classNames={{ label: "text-sm md:text-base text-gray-500" }}
            />
          </div>
          <div className="flex space-x-2 mt-4">
            <Button
              className="w-1/2"
              color="success"
              isDisabled={!accepted}
              isLoading={updateProfileTermsStatusLoading}
              onPress={onAcceptHandle}
            >
              Agree and Continue
            </Button>
            <Button className="w-1/2" color="danger" onPress={handleDisagree}>
              Disagree and logout
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default UserConsentModal;
